import React, { useState, useEffect } from 'react';
import { apiFetch } from '../api/api';
import './Analysis.css';

const Analysis = () => {
  const [configData, setConfigData] = useState([]);
  const [tableData, setTableData] = useState({});
  const [selectedTable, setSelectedTable] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({});
  const [chartData, setChartData] = useState({});

  // Load config_column data and initial statistics
  useEffect(() => {
    loadConfigData();
  }, []);

  // Load table data when selected table changes
  useEffect(() => {
    if (selectedTable) {
      loadTableData(selectedTable);
    }
  }, [selectedTable]);

  const loadConfigData = async () => {
    try {
      setLoading(true);
      const config = await apiFetch('/api/config/columns');
      setConfigData(config);
      
      // Load statistics for all tables
      await loadStatistics(config);
      
      // Set first table as default
      if (config.length > 0) {
        setSelectedTable(config[0].tableName);
      }
    } catch (err) {
      console.error('Failed to load config data:', err);
      setError('Failed to load configuration data');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async (config) => {
    try {
      const statsPromises = config.map(async (table) => {
        try {
          const data = await apiFetch(`/api/db/${table.tableName}`);
          return {
            tableName: table.tableName,
            count: Array.isArray(data) ? data.length : 0,
            data: Array.isArray(data) ? data : []
          };
        } catch (err) {
          console.warn(`Failed to load data for ${table.tableName}:`, err);
          return {
            tableName: table.tableName,
            count: 0,
            data: []
          };
        }
      });

      const results = await Promise.all(statsPromises);
      const statsObj = {};
      const chartDataObj = {};

      results.forEach(result => {
        statsObj[result.tableName] = {
          count: result.count,
          data: result.data
        };

        // Generate chart data for numerical fields
        if (result.data.length > 0) {
          const tableConfig = config.find(t => t.tableName === result.tableName);
          const numericalColumns = tableConfig?.columns?.filter(col => 
            ['decimal2', 'decimal4', 'float', 'comma', 'currency'].includes(col.type)
          ) || [];

          chartDataObj[result.tableName] = generateChartData(result.data, numericalColumns);
        }
      });

      setStats(statsObj);
      setChartData(chartDataObj);
    } catch (err) {
      console.error('Failed to load statistics:', err);
    }
  };

  const loadTableData = async (tableName) => {
    try {
      const data = await apiFetch(`/api/db/${tableName}`);
      setTableData(prev => ({
        ...prev,
        [tableName]: Array.isArray(data) ? data : []
      }));
    } catch (err) {
      console.error(`Failed to load ${tableName} data:`, err);
      setTableData(prev => ({
        ...prev,
        [tableName]: []
      }));
    }
  };

  const generateChartData = (data, numericalColumns) => {
    const charts = {};

    numericalColumns.forEach(column => {
      const values = data
        .map(row => parseFloat(row[column.name]))
        .filter(val => !isNaN(val));

      if (values.length > 0) {
        const min = Math.min(...values);
        const max = Math.max(...values);
        const avg = values.reduce((sum, val) => sum + val, 0) / values.length;

        // Create histogram data
        const bins = 10;
        const binSize = (max - min) / bins;
        const histogram = new Array(bins).fill(0);

        values.forEach(value => {
          const binIndex = Math.min(Math.floor((value - min) / binSize), bins - 1);
          histogram[binIndex]++;
        });

        charts[column.name] = {
          label: column.label,
          unit: column.unit,
          min,
          max,
          avg,
          count: values.length,
          histogram,
          binLabels: histogram.map((_, i) => {
            const binStart = min + (i * binSize);
            const binEnd = min + ((i + 1) * binSize);
            return `${binStart.toFixed(1)}-${binEnd.toFixed(1)}`;
          })
        };
      }
    });

    return charts;
  };

  const formatValue = (value, type, unit = '') => {
    if (value === null || value === undefined) return '-';

    switch (type) {
      case 'currency':
        return `$${Number(value).toLocaleString()}`;
      case 'comma':
        return Number(value).toLocaleString();
      case 'decimal2':
        return `${Number(value).toFixed(2)} ${unit}`.trim();
      case 'decimal4':
        return `${Number(value).toFixed(4)} ${unit}`.trim();
      case 'float':
        return `${Number(value).toFixed(1)} ${unit}`.trim();
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'datetime':
        return new Date(value).toLocaleString();
      default:
        return `${value} ${unit}`.trim();
    }
  };

  const getTableConfig = (tableName) => {
    return configData.find(table => table.tableName === tableName);
  };

  const renderOverviewCards = () => {
    return (
      <div className="overview-cards">
        {configData.map(table => (
          <div key={table.tableName} className="overview-card">
            <h3>{table.tableName}</h3>
            <div className="card-stat">
              <span className="stat-number">{stats[table.tableName]?.count || 0}</span>
              <span className="stat-label">Records</span>
            </div>
            <button 
              className={`view-details-btn ${selectedTable === table.tableName ? 'active' : ''}`}
              onClick={() => setSelectedTable(table.tableName)}
            >
              View Details
            </button>
          </div>
        ))}
      </div>
    );
  };

  const renderTableAnalysis = () => {
    if (!selectedTable) return null;

    const tableConfig = getTableConfig(selectedTable);
    const data = tableData[selectedTable] || [];
    const charts = chartData[selectedTable] || {};

    return (
      <div className="table-analysis">
        <h2>{selectedTable} Analysis</h2>
        
        {/* Summary Statistics */}
        <div className="summary-stats">
          <div className="stat-card">
            <h4>Total Records</h4>
            <span className="stat-value">{data.length}</span>
          </div>
          <div className="stat-card">
            <h4>Columns</h4>
            <span className="stat-value">{tableConfig?.columns?.length || 0}</span>
          </div>
          <div className="stat-card">
            <h4>Numerical Fields</h4>
            <span className="stat-value">
              {tableConfig?.columns?.filter(col => 
                ['decimal2', 'decimal4', 'float', 'comma', 'currency'].includes(col.type)
              ).length || 0}
            </span>
          </div>
        </div>

        {/* Numerical Field Charts */}
        {Object.keys(charts).length > 0 && (
          <div className="charts-section">
            <h3>Numerical Field Analysis</h3>
            <div className="charts-grid">
              {Object.entries(charts).map(([fieldName, chartInfo]) => (
                <div key={fieldName} className="chart-card">
                  <h4>{chartInfo.label} {chartInfo.unit && `(${chartInfo.unit})`}</h4>
                  <div className="chart-stats">
                    <div className="stat-row">
                      <span>Min:</span>
                      <span>{formatValue(chartInfo.min, 'decimal2', chartInfo.unit)}</span>
                    </div>
                    <div className="stat-row">
                      <span>Max:</span>
                      <span>{formatValue(chartInfo.max, 'decimal2', chartInfo.unit)}</span>
                    </div>
                    <div className="stat-row">
                      <span>Avg:</span>
                      <span>{formatValue(chartInfo.avg, 'decimal2', chartInfo.unit)}</span>
                    </div>
                    <div className="stat-row">
                      <span>Count:</span>
                      <span>{chartInfo.count}</span>
                    </div>
                  </div>
                  
                  {/* Simple histogram */}
                  <div className="histogram">
                    <div className="histogram-bars">
                      {chartInfo.histogram.map((count, index) => (
                        <div 
                          key={index}
                          className="histogram-bar"
                          style={{ 
                            height: `${(count / Math.max(...chartInfo.histogram)) * 100}%` 
                          }}
                          title={`${chartInfo.binLabels[index]}: ${count} records`}
                        />
                      ))}
                    </div>
                    <div className="histogram-labels">
                      {chartInfo.binLabels.map((label, index) => (
                        <span key={index} className="histogram-label">{label}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="data-table-section">
          <h3>Recent Data ({Math.min(data.length, 10)} of {data.length} records)</h3>
          {data.length > 0 ? (
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    {tableConfig?.columns
                      ?.sort((a, b) => (a.order || 0) - (b.order || 0))
                      ?.slice(0, 8) // Show first 8 columns
                      ?.map(column => (
                        <th key={column.name}>
                          {column.label}
                          {column.unit && <span className="unit"> ({column.unit})</span>}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {data.slice(0, 10).map((row, index) => (
                    <tr key={index}>
                      {tableConfig?.columns
                        ?.sort((a, b) => (a.order || 0) - (b.order || 0))
                        ?.slice(0, 8)
                        ?.map(column => (
                          <td key={column.name}>
                            {formatValue(row[column.name], column.type, column.unit)}
                          </td>
                        ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">No data available for {selectedTable}</div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="analysis-container">
        <div className="loading-spinner">Loading analysis data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analysis-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="analysis-container">
      <div className="analysis-header">
        <h1>Data Analysis Dashboard</h1>
        <p>Interactive analysis of drilling data based on configuration</p>
      </div>

      {/* Overview Section */}
      <section className="overview-section">
        <h2>Data Overview</h2>
        {renderOverviewCards()}
      </section>

      {/* Detailed Analysis Section */}
      <section className="detailed-analysis-section">
        {renderTableAnalysis()}
      </section>
    </div>
  );
};

export default Analysis; 